import { Box, Typography, useTheme, Button, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import { useState, useEffect, useMemo, useRef } from "react";
import { tokens } from "../../theme";
import { useNavigate, useLocation } from "react-router-dom";
import useResultService from "../../api/services/ResultsService";
import LoadingSpinner from "../../components/LoadingSpinner";
import generateClassResultsPDF from "../../utils/classResultsPdf";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

// ---------- CORE SUBJECTS USED FOR DIVISION ----------
const CORE_SUBJECTS = [
  "PHYSICS",
  "CHEMISTRY",
  "BIOLOGY",
  "ADVANCED MATHEMATICS",
  "ECONOMICS",
  "HISTORY",
  "GEOGRAPHY",
  "ENGLISH LANGUAGE",
];

// ---------- Grades ----------
const getGrade = (marks) => {
  if (marks >= 80) return "A";
  if (marks >= 70) return "B";
  if (marks >= 60) return "C";
  if (marks >= 50) return "D";
  if (marks >= 40) return "E";
  if (marks >= 35) return "S";
  return "F";
};

// ---------- Grade Points ----------
const getGradePoint = (grade) =>
  ({ A: 1, B: 2, C: 3, D: 4, E: 5, S: 6, F: 7 }[grade] || 0);

// ---------- Division Points (ONLY CORE SUBJECTS) ----------
// Use grade for division; treat empty/null grade as F (same as initial behaviour when null marks → 0 → F)
const determineDivisionPoints = (subjectsObj) => {
  const points = Object.entries(subjectsObj)
    .filter(([subject]) => CORE_SUBJECTS.includes(subject.toUpperCase()))
    .map(([_, s]) => getGradePoint(s.grade || "F"));

  if (points.length === 0) return 0;

  const usePoints =
    points.length >= 3 ? points.sort((a, b) => a - b).slice(0, 3) : points;

  return usePoints.reduce((a, b) => a + b, 0);
};

const getDivisionLabel = (points) => {
  const p = parseFloat(points).toFixed(1).replace(/\.0$/, "");
  if (points >= 1 && points <= 9) return `Division 1 .${p}`;
  if (points >= 10 && points <= 12) return `Division 2 .${p}`;
  if (points >= 13 && points <= 17) return `Division 3 .${p}`;
  if (points >= 18 && points <= 21) return `Division 4 .${p}`;
  return `Division 0 .${p}`;
};

const AlevelClassResultsMarks100 = () => {
  const location = useLocation();
  const classId = location.state?.classId || location.state?.id;
  const examinationTypeId = location.state?.examId;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const { fetchClassResults, sendSmsToParents } = useResultService();

  // ✅ prevent infinite loops if service functions change reference
  const fetchClassResultsRef = useRef(fetchClassResults);
  const sendSmsToParentsRef = useRef(sendSmsToParents);

  useEffect(() => {
    fetchClassResultsRef.current = fetchClassResults;
  }, [fetchClassResults]);

  useEffect(() => {
    sendSmsToParentsRef.current = sendSmsToParents;
  }, [sendSmsToParents]);

  // ✅ store ALL results (unfiltered)
  const [allResults, setAllResults] = useState([]);

  // ✅ filter view (default ACTIVE)
  const [statusView, setStatusView] = useState("ACTIVE"); // "ACTIVE" | "ALL"

  const [isLoading, setIsLoading] = useState(false);
  const [sendingStatus, setSendingStatus] = useState({});
  const [sentStatus, setSentStatus] = useState({});

  // Load sent status from localStorage
  useEffect(() => {
    const savedSentStatus = localStorage.getItem(
      `sentStatus_${classId}_${examinationTypeId}`
    );
    if (savedSentStatus) {
      setSentStatus(JSON.parse(savedSentStatus));
    }
  }, [classId, examinationTypeId]);

  // Reset to ACTIVE when class/exam changes
  useEffect(() => {
    setStatusView("ACTIVE");
  }, [classId, examinationTypeId]);

  // ---------- Fetch Results ----------
  useEffect(() => {
    let mounted = true;

    const loadResults = async () => {
      if (!classId || !examinationTypeId) return;

      setIsLoading(true);
      try {
        const data = await fetchClassResultsRef.current(classId, examinationTypeId);

        if (!mounted) return;

        if (data && Array.isArray(data.data)) {
          const studentMap = {};

          data.data.forEach((item) => {
            if (!studentMap[item.studentId]) {
              studentMap[item.studentId] = {
                id: item.studentId,
                studentName: item.studentName,
                studentNumber: item.studentNumber,
                combination: item.combination || "N/A",
                className: item.className,
                classTitle: item.classTitle,
                examinationType: item.examinationType,
                // ✅ for ACTIVE/ALL filtering
                studentStatus: item.studentStatus || item.status || "",
                subjects: {},
              };
            }

            // Keep null for unfilled; 0 is valid and included in average
            const marksRaw = item.marks != null && item.marks !== "" ? parseFloat(item.marks) : null;
            const marks = marksRaw !== null && !isNaN(marksRaw) ? marksRaw : null;
            const grade = marks !== null ? (item.grade || getGrade(marks)) : "";

            studentMap[item.studentId].subjects[item.subjectName] = {
              marks,
              grade,
            };
          });

          let processed = Object.values(studentMap).map((student) => {
            // Include subjects with numeric mark (including 0); exclude null
            const validSubjects = Object.values(student.subjects).filter(
              (s) => s?.marks !== null && s?.marks !== undefined
            );

            const subjectMarks = validSubjects.map((s) => s.marks ?? 0);
            const totalMarksRaw = subjectMarks.reduce((sum, m) => sum + m, 0);

            const count = validSubjects.length;
            const totalMarks = parseFloat(totalMarksRaw.toFixed(1));

            const hasCompleteResults = count >= 4;
            const average = hasCompleteResults
              ? parseFloat((totalMarks / count).toFixed(1))
              : "Incomplete";

            const points = determineDivisionPoints(student.subjects);
            const division = getDivisionLabel(points);

            return {
              ...student,
              totalMarks,
              average,
              division,
              subjectCount: count,
              hasCompleteResults,
            };
          });

          // sort complete first by average; incomplete by subjectCount then totalMarks
          const completeResults = processed.filter((s) => s.hasCompleteResults);
          const incompleteResults = processed.filter((s) => !s.hasCompleteResults);

          completeResults.sort((a, b) => (b.average || 0) - (a.average || 0));

          incompleteResults.sort((a, b) => {
            if (b.subjectCount !== a.subjectCount) return b.subjectCount - a.subjectCount;
            return (b.totalMarks || 0) - (a.totalMarks || 0);
          });

          const sorted = [...completeResults, ...incompleteResults].map((stu, idx) => ({
            ...stu,
            position: idx + 1,
          }));

          setAllResults(sorted);
        } else {
          setAllResults([]);
        }
      } catch (e) {
        console.error("Error loading results:", e);
        if (mounted) setAllResults([]);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadResults();

    return () => {
      mounted = false;
    };
  }, [classId, examinationTypeId]);

  // ✅ view results (ACTIVE or ALL) + recompute position within the view
  const results = useMemo(() => {
    const isActive = (s) =>
      String(s || "")
        .trim()
        .toUpperCase() === "ACTIVE";

    const base =
      statusView === "ALL"
        ? allResults
        : allResults.filter((s) => isActive(s.studentStatus));

    return base.map((stu, idx) => ({
      ...stu,
      position: idx + 1,
    }));
  }, [allResults, statusView]);

  const meta = allResults[0] || results[0] || {};

  const handleViewStudent = (row) => {
    navigate(`/alevel_student_results_marks/${row.id}/${classId}/${examinationTypeId}`);
  };

  const handleSendSMS = async (row) => {
    const studentId = row.id;
    setSendingStatus((prev) => ({ ...prev, [studentId]: true }));

    try {
      const subjectsSummary = Object.entries(row.subjects || {}).reduce(
        (acc, [subject, details]) => {
          const marks =
            details?.marks !== undefined && details?.marks !== null
              ? parseFloat(details.marks) || 0
              : 0;
          const grade = details?.grade || "";
          acc[subject] = { marks, grade };
          return acc;
        },
        {}
      );

      const payload = {
        studentName: row.studentName,
        studentNumber: row.studentNumber,
        subjects: subjectsSummary,
        totalMarks: parseFloat(row.totalMarks) || 0,
        average:
          row.average === "Incomplete"
            ? "Incomplete"
            : parseFloat(row.average?.toFixed?.(1)) || 0,
        position: row.position ? parseInt(row.position) : 0,
        division: row.division || "N/A",
        totalStudents: results.length, // ✅ view total (ACTIVE or ALL)
        className: row.className || "N/A",
        examinationType: row.examinationType || "N/A",
      };

      await sendSmsToParentsRef.current(studentId, payload);

      setSentStatus((prev) => {
        const newStatus = { ...prev, [studentId]: true };
        localStorage.setItem(
          `sentStatus_${classId}_${examinationTypeId}`,
          JSON.stringify(newStatus)
        );
        return newStatus;
      });
    } catch (error) {
      console.error("Error sending SMS", error);
    } finally {
      setSendingStatus((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const createColumns = () => {
    if (!results.length) return [];

    const subjectSet = new Set();
    results.forEach((student) =>
      Object.keys(student.subjects || {}).forEach((s) => subjectSet.add(s))
    );
    const subjectNames = Array.from(subjectSet);

    const columns = [
      {
        field: "studentName",
        headerName: "Student Name",
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.studentName}</Typography>
        ),
      },
      {
        field: "studentNumber",
        headerName: "Index Number",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.studentNumber}</Typography>
        ),
      },
      {
        field: "combination",
        headerName: "Combination",
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.combination}</Typography>
        ),
      },
    ];

    subjectNames.forEach((subject) => {
      columns.push({
        field: `sub_${subject}`,
        headerName: subject,
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => {
          // Case-insensitive lookup so marks show even if subject name casing differs
          const subjectKey = Object.keys(row.subjects || {}).find(
            (k) => k.toUpperCase() === subject.toUpperCase()
          );
          const s = subjectKey ? row.subjects[subjectKey] : undefined;
          const marks = s?.marks !== undefined && s?.marks !== null ? Number(s.marks) : null;
          const grade = s?.grade ?? "";

          // null = blank; 0 = show (including 0 with grade F)
          if (marks === null) return <Box />;

          const isFail = grade === "F" || (typeof marks === "number" && marks < 35);
          return (
            <Box>
              <Typography
                component="span"
                sx={{
                  fontWeight: 500,
                  ...(isFail && { color: "error.main" }),
                }}
              >
                {marks}
              </Typography>
              {grade ? (
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.75rem",
                    opacity: 0.8,
                    ml: 0.5,
                    ...(isFail && { color: "error.main" }),
                  }}
                >
                  ({grade})
                </Typography>
              ) : null}
            </Box>
          );
        },
      });
    });

    columns.push(
      {
        field: "totalMarks",
        headerName: "Total",
        flex: 0.6,
        minWidth: 90,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.totalMarks}</Typography>
        ),
      },
      {
        field: "average",
        headerName: "Average",
        flex: 0.6,
        minWidth: 90,
        renderCell: ({ row }) => (
          <Typography
            fontWeight="bold"
            color={row.average === "Incomplete" ? "error" : "inherit"}
          >
            {row.average}
          </Typography>
        ),
      },
      {
        field: "division",
        headerName: "Division",
        flex: 0.7,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Box>
            <Typography fontWeight="bold" variant="body2">
              {String(row.division || "").split(" (")[0]}
            </Typography>
          </Box>
        ),
      },
      {
        field: "position",
        headerName: "Position",
        flex: 0.5,
        minWidth: 110,
        renderCell: ({ row }) => (
          <Typography
            fontWeight="bold"
            color={!row.hasCompleteResults ? "error" : "inherit"}
          >
            {row.position} out of {results.length}
            {!row.hasCompleteResults && " *"}
          </Typography>
        ),
      },
      {
        field: "action",
        headerName: "Action",
        flex: 0.8,
        minWidth: 180,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton color="secondary" onClick={() => handleViewStudent(row)}>
              <VisibilityOutlinedIcon />
            </IconButton>

            <Button
              variant="contained"
              color="secondary" // ✅ default secondary
              size="small"
              disabled={sendingStatus[row.id]}
              onClick={() => handleSendSMS(row)}
              sx={{
                ...(sentStatus[row.id] && {
                  backgroundColor: "#0A6927",
                  color: "white",
                  "&:hover": { backgroundColor: "#085A1F" },
                }),
                "&:disabled": {
                  backgroundColor: sendingStatus[row.id] ? "#ccc" : undefined,
                },
              }}
            >
              {sendingStatus[row.id]
                ? "Sending..."
                : sentStatus[row.id]
                ? "Sent"
                : "Send SMS"}
            </Button>
          </Box>
        ),
      }
    );

    return columns;
  };

  return (
    <Box m="20px">
      <Header
        title={`CLASS RESULTS FOR: ${meta.className || "N/A"} | CLASS TITLE: ${
          meta.classTitle || "N/A"
        } | EXAM: ${meta.examinationType || "N/A"}`}
      />

      {/* ✅ Top-right: ACTIVE / ALL + Download PDF */}
      <Box mb={2} display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
        <Button
          variant={statusView === "ACTIVE" ? "contained" : "outlined"}
          color="secondary"
          onClick={() => setStatusView("ACTIVE")}
        >
          ACTIVE
        </Button>

        <Button
          variant={statusView === "ALL" ? "contained" : "outlined"}
          color="secondary"
          onClick={() => setStatusView("ALL")}
        >
          ALL
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={() =>
            generateClassResultsPDF(
              results, // ✅ export what is currently displayed (ACTIVE or ALL)
              meta.className,
              meta.examinationType,
              meta.classTitle
            )
          }
          disabled={!results || results.length === 0}
          sx={{ ml: 1 }}
        >
          Download PDF
        </Button>
      </Box>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Box sx={{ height: "75vh" }}>
          <DataGrid rows={results} columns={createColumns()} getRowHeight={() => 80} pageSize={10} />
        </Box>
      )}
    </Box>
  );
};

export default AlevelClassResultsMarks100;
